import { useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";

import {
  formatDate,
  formatDuration,
  formatPrescriptionMethod,
  formatSessionRole,
  formatSport,
  formatTime,
} from "@/lib/coachingFormatters";
import { prescriptionIntensityLabel } from "@/lib/prescriptionIntensity";
import { WorkoutPrescriptionDetail } from "@/components/coaching/WorkoutPrescriptionDetail";
import { Button } from "@/components/ui/button";

export const ScheduledWorkoutCard = ({
  session,
  timezone,
  testIdPrefix = "scheduled-workout",
  defaultExpanded = false,
  onEdit,
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);

  if (!session) return null;
  const effort = prescriptionIntensityLabel(session) || formatSessionRole(session.role);

  return (
    <div
      className="border border-dark-border rounded-md p-4 bg-dark-surface-2"
      data-testid={`${testIdPrefix}-${session.session_id || "session"}`}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="font-heading text-base tracking-wide">
            {session.workout_name || "Session"}
          </div>
          <div className="text-xs text-dark-text-secondary mt-1">
            {formatSport(session.sport)}
            {" · "}
            <span data-testid={`${testIdPrefix}-effort`}>{effort}</span>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {session.is_editable && onEdit && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => onEdit(session)}
              data-testid={`${testIdPrefix}-edit-${session.session_id || "session"}`}
            >
              Edit workout
            </Button>
          )}
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => setExpanded((current) => !current)}
            data-testid={`${testIdPrefix}-toggle-${session.session_id || "session"}`}
            aria-expanded={expanded}
          >
            {expanded ? (
              <>
                Hide details
                <ChevronUp className="w-4 h-4" aria-hidden="true" />
              </>
            ) : (
              <>
                View workout
                <ChevronDown className="w-4 h-4" aria-hidden="true" />
              </>
            )}
          </Button>
        </div>
      </div>

      <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-1 text-xs">
        <div>
          <div className="text-dark-text-muted uppercase tracking-widest text-[10px]">
            Scheduled
          </div>
          <div>
            {session.scheduled_date
              ? formatDate(session.scheduled_date, timezone)
              : "—"}
          </div>
        </div>
        <div>
          <div className="text-dark-text-muted uppercase tracking-widest text-[10px]">
            Start
          </div>
          <div>{formatTime(session.planned_start_time, timezone)}</div>
        </div>
        <div>
          <div className="text-dark-text-muted uppercase tracking-widest text-[10px]">
            Duration
          </div>
          <div>{formatDuration(session.duration_minutes)}</div>
        </div>
        <div>
          <div className="text-dark-text-muted uppercase tracking-widest text-[10px]">
            Prescription
          </div>
          <div>{formatPrescriptionMethod(session.prescription_method)}</div>
        </div>
        <div>
          <div className="text-dark-text-muted uppercase tracking-widest text-[10px]">
            Target
          </div>
          <div>{session.intensity_target_summary || session.intensity_summary || "—"}</div>
        </div>
      </div>

      {expanded && <WorkoutPrescriptionDetail session={session} />}
    </div>
  );
};
